/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Levels Node
   Photoshop-style tonal adjustment: black point, white point, and gamma.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const LEVELS_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform float u_inBlack;
uniform float u_inWhite;
uniform float u_gamma;
uniform float u_outBlack;
uniform float u_outWhite;

void main() {
  vec4 src = texture(u_image, v_uv);
  
  // 1. Remap input range
  vec3 rgb = clamp((src.rgb - vec3(u_inBlack)) / max(u_inWhite - u_inBlack, 0.0001), 0.0, 1.0);
  
  // 2. Apply gamma curve
  rgb = pow(rgb, vec3(1.0 / max(u_gamma, 0.001)));
  
  // 3. Remap output range
  rgb = mix(vec3(u_outBlack), vec3(u_outWhite), rgb);
  
  fragColor = vec4(clamp(rgb, 0.0, 1.0), src.a);
}
`;

export const levelsNode: NodeDefinition = {
  type: 'color.levels',
  category: 'Color',
  label: 'Levels',
  description: 'Remaps tonal range, black/white points, and midtone gamma curves',
  keywords: ['levels', 'tonal', 'gamma', 'histogram', 'black', 'white', 'contrast'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Output', type: 'image' },
  ],
  params: [
    {
      id: 'inBlack',
      name: 'Input Black',
      type: 'slider',
      defaultValue: 0,
      min: 0,
      max: 255,
      step: 1,
    },
    {
      id: 'inWhite',
      name: 'Input White',
      type: 'slider',
      defaultValue: 255,
      min: 0,
      max: 255,
      step: 1,
    },
    {
      id: 'gamma',
      name: 'Midtone Gamma',
      type: 'slider',
      defaultValue: 1.0,
      min: 0.1,
      max: 5.0,
      step: 0.05,
      precision: 2,
    },
    {
      id: 'outBlack',
      name: 'Output Black',
      type: 'slider',
      defaultValue: 0,
      min: 0,
      max: 255,
      step: 1,
    },
    {
      id: 'outWhite',
      name: 'Output White',
      type: 'slider',
      defaultValue: 255,
      min: 0,
      max: 255,
      step: 1,
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: LEVELS_SHADER,
    getUniforms: (inputs, params) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_inBlack: Number(params.inBlack ?? 0) / 255,
      u_inWhite: Number(params.inWhite ?? 255) / 255,
      u_gamma: Number(params.gamma ?? 1.0) || 1.0,
      u_outBlack: Number(params.outBlack ?? 0) / 255,
      u_outWhite: Number(params.outWhite ?? 255) / 255,
    }),
  }),
};
